import { Visibility } from '@mui/icons-material'
import { Box, IconButton, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import React from 'react'

interface OrganizeSectionRowProps {
  displayText: string
  ancestorPath: string
  isSelected?: boolean
  isDimmed?: boolean
  isPickedFrom?: boolean
  hasViolation?: boolean
  /** Relation to the currently focused (hovered) section: must come before it, or must come after it. */
  relationToFocus?: 'before' | 'after'
  bookFontFamily?: string
  bookDirection?: 'ltr' | 'rtl'
  onPreview?: (event: React.MouseEvent) => void
  /** Compact right-aligned rail (ratings + tag dots). */
  tagRail?: React.ReactNode
  /** Compact dependency chip rendered next to the rail. */
  dependencyChips?: React.ReactNode
}

export default function OrganizeSectionRow({
  displayText,
  ancestorPath,
  isSelected,
  isDimmed,
  isPickedFrom,
  hasViolation,
  relationToFocus,
  bookFontFamily,
  bookDirection = 'ltr',
  onPreview,
  tagRail,
  dependencyChips,
}: OrganizeSectionRowProps) {
  const outlineColor = isPickedFrom
    ? 'primary.main'
    : hasViolation
      ? 'error.main'
      : isSelected
        ? 'primary.main'
        : 'transparent'

  return (
    <Box
      onContextMenu={(ev) => ev.preventDefault()}
      sx={(theme) => {
        const relationBg =
          relationToFocus === 'before'
            ? alpha(theme.palette.info.main, 0.28)
            : relationToFocus === 'after'
              ? alpha(theme.palette.warning.main, 0.28)
              : undefined
        const relationEdgeColor =
          relationToFocus === 'before'
            ? theme.palette.info.main
            : relationToFocus === 'after'
              ? theme.palette.warning.main
              : 'transparent'
        const baseBg = isPickedFrom
          ? alpha(theme.palette.primary.main, 0.18)
          : isSelected
            ? theme.palette.action.selected
            : 'transparent'
        return {
          px: 2,
          py: 0.75,
          mx: 1,
          borderRadius: 1,
          cursor: 'grab',
          transition: 'background-color 0.12s ease, border-color 0.12s ease',
          bgcolor: relationBg ?? baseBg,
          outline: isPickedFrom ? '2px solid' : isSelected || hasViolation ? '1px solid' : '1px solid transparent',
          outlineColor,
          opacity: isDimmed ? 0.35 : 1,
          borderInlineStart: '3px solid',
          borderInlineStartColor: relationEdgeColor,
          '&:hover': {
            bgcolor:
              relationBg ?? (isPickedFrom ? alpha(theme.palette.primary.main, 0.24) : theme.palette.action.hover),
            opacity: isDimmed ? 0.7 : 1,
          },
          '&:hover .preview-btn': {
            opacity: 0.9,
          },
          '&:active': {
            cursor: 'grabbing',
          },
          userSelect: 'none',
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontFamily: bookFontFamily || 'SBL Hebrew',
            fontSize: '0.875rem',
            lineHeight: 1.5,
            direction: bookDirection,
            textAlign: bookDirection === 'rtl' ? 'right' : 'left',
            fontWeight: 400,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayText}
        </Typography>
        {dependencyChips}
        {tagRail}
        {onPreview && (
          <IconButton
            className="preview-btn"
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              onPreview(e)
            }}
            title="Preview section"
            sx={{
              flexShrink: 0,
              p: 0.25,
              opacity: 0.25,
              color: 'text.secondary',
              transition: 'opacity 0.15s ease',
              '&:hover': { bgcolor: 'action.hover', opacity: 1 },
            }}
          >
            <Visibility sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>
      {ancestorPath && (
        <Typography
          sx={{
            fontSize: '0.7rem',
            lineHeight: 1.3,
            color: 'text.secondary',
            opacity: 0.7,
            direction: bookDirection,
            textAlign: bookDirection === 'rtl' ? 'right' : 'left',
            mt: 0.25,
          }}
        >
          {ancestorPath}
        </Typography>
      )}
    </Box>
  )
}
